// `slide-translation-vibes-chart` + `slide-translation-chart-german`, drawn by Mermaid from
// docs/PRESENTATION.md's three successive ```mermaid blocks under "On language" (task 4.2/4.3):
//
//   step 0 (base):     My brain -[Vibes]-> English -[Translation]-> Ancient Greek -[Vibes]-> Your brain
//   step 1 (German):   the "Translation" edge splits into a hard branch (Ancient Greek — "which
//                      love?") and an easy one (German — "same laziness"), converging back on Your brain
//   step 2 (hug):      + a third branch, My brain -[Just vibe]-> A hug -[Vibes]-> Your brain
//
// **One component, two slide ids.** The script shows the chart, interrupts it with the love
// example, and then "the translation chart returns, now with German added" — so the return is
// mounted at a base step of 1 (`SlideDeck.tsx`) rather than duplicated as a second component with
// the same three states in it.
//
// **The hug is a third branch of the same graph, not a second diagram** (2026-08-11). It used to be
// its own row because the hand-built diagram kit could not lay out a three-way branch; a real graph
// engine can, so adding it now *relayouts the whole chart* on its click — which is the entire reason
// Mermaid earns a place in the bundle. Every outgoing "meaning" edge is the one word **Vibes**: the
// presenter's point is that Greek, German and a hug all arrive in the same place.

import type { ReactElement } from 'react';
import { SlideFrame } from './SlideFrame';
import { MermaidDiagram } from './MermaidDiagram';
import { TRANSLATION_CHART_MAX_REVEAL } from './content';

/**
 * The chart's Mermaid source at a given reveal step — the slide's test seam. Pure, and clamped the
 * same way the slide is, so an out-of-range step yields the final state rather than throwing.
 *
 * Every node and edge label the audience reads is in this string, which is also what the slide
 * renders as its fallback (`MermaidDiagram`), so a text assertion over it is an assertion about the
 * drawn diagram.
 */
export function translationChartMermaid(revealStep: number): string {
  const step = Math.max(0, Math.min(revealStep, TRANSLATION_CHART_MAX_REVEAL));
  const lines = ['flowchart LR', '  brain(["My brain"]) -->|"Vibes"| english["English"]'];

  if (step === 0) {
    lines.push('  english -->|"Translation"| greek["Ancient Greek"]');
    lines.push('  greek -->|"Vibes"| you(["Your brain"])');
  } else {
    lines.push('  english -->|"Harder translation"| greek["Ancient Greek"]');
    lines.push('  english -->|"Easier translation"| german["German"]');
    lines.push('  greek -->|"Vibes"| you(["Your brain"])');
    lines.push('  german -->|"Vibes"| you');
  }

  if (step >= 2) {
    lines.push('  brain -->|"Just vibe"| hug["A hug"]');
    lines.push('  hug -->|"Vibes"| you');
  }
  return lines.join('\n');
}

export function TranslationChartSlide({ revealStep }: { revealStep: number }): ReactElement {
  const step = Math.max(0, Math.min(revealStep, TRANSLATION_CHART_MAX_REVEAL));
  return (
    <SlideFrame>
      <MermaidDiagram
        source={translationChartMermaid(step)}
        revealTestId={step >= 2 ? 'reveal-hug' : undefined}
      />
    </SlideFrame>
  );
}
